import React, { useState } from 'react';
import useWebPushStatus from '../notifications/useWebPushStatus';
import PwaInstallHelpSheet from '../installButton/PwaInstallHelpSheet';
import { getPwaInstallRecoverySnapshot } from '../installButton/usePwaInstallRecovery';
import '../notifications/jgAlertsActivation.css';

export default function PushSubscribeButton({
  jwt,
  vapidKey,
  className = '',
  copy = {},
  product,
  onStateChange,
  onSubscribed,
  variant = 'default',
  buttonRef = null,
}) {
  const status = useWebPushStatus({ jwt, onStateChange, onSubscribed });
  const [manage, setManage] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const localops = product === 'localops';
  const help =
    copy.helpText ||
    (localops
      ? 'Get notified about nearby tasks and important updates, even when Joe Galaxy is closed.'
      : 'Get notified about messages, requests, LocalOps tasks, and important updates.');
  const messages = {
    checking: 'Checking alerts on this device...',
    connecting: 'Connecting alerts...',
    disconnecting: 'Turning off alerts...',
    enabled: 'Alerts enabled on this device',
    denied:
      'Notifications are blocked. Allow notifications for joegalaxy.net in your phone or browser settings.',
    install: 'Add to Home Screen \u2192 Open the app \u2192 Enable alerts',
    unsupported: 'Alerts are unavailable in this browser or app.',
    signin: 'Sign in to enable alerts.',
    unavailable: 'Alerts are temporarily unavailable. Try again later.',
    off: 'Alerts are off. Choose Enable alerts to turn them on.',
    reconnect:
      "We couldn't confirm alerts on this device. Reconnect alerts to try again.",
    worker: 'Alerts are still setting up. Try again.',
    enable: help,
  };
  const unavailableKey =
    !vapidKey && ['enable', 'off', 'reconnect'].includes(status.phase);
  const label =
    status.phase === 'disable-error'
      ? 'Turn off alerts on this device'
      : status.phase === 'install'
      ? 'Add to Home Screen'
      : status.phase === 'reconnect'
      ? 'Reconnect alerts'
      : status.phase === 'worker'
      ? 'Check alerts again'
      : copy.enableLabel || 'Enable alerts';
  const disabled =
    status.busy ||
    ['checking', 'denied', 'unsupported', 'signin', 'unavailable'].includes(
      status.phase,
    ) ||
    unavailableKey;
  const message = unavailableKey
    ? messages.unavailable
    : status.reason || messages[status.phase] || help;

  return (
    <div
      className={`jgPushControl ${
        variant === 'compact' ? 'jgPushControl--compact' : ''
      } ${className}`}
    >
      <div
        role="status"
        aria-live="polite"
        className={
          status.subscribed
            ? 'jgPushStatus jgPushStatus--enabled'
            : 'jgPushStatus'
        }
      >
        {message}
      </div>
      {status.subscribed ? (
        <>
          <button
            type="button"
            className="jgPushManage"
            aria-expanded={manage}
            onClick={() => setManage((value) => !value)}
          >
            Manage alerts
          </button>
          {manage && (
            <button
              type="button"
              className="jgPushManage"
              onClick={status.disablePush}
              disabled={status.busy}
            >
              Turn off alerts on this device
            </button>
          )}
        </>
      ) : (
        <button
          type="button"
          ref={buttonRef}
          className="jgPushAction"
          disabled={disabled}
          onClick={() => {
            if (status.phase === 'install') setInstallOpen(true);
            else if (status.phase === 'disable-error') status.disablePush();
            else if (status.phase === 'worker') status.refresh();
            else status.enablePush(vapidKey);
          }}
        >
          {status.busy ? messages[status.phase] : label}
        </button>
      )}
      {status.phase === 'install' && (
        <PwaInstallHelpSheet
          open={installOpen}
          onClose={() => setInstallOpen(false)}
          platform={getPwaInstallRecoverySnapshot().platform}
          canNativeInstall={false}
        />
      )}
    </div>
  );
}
